// @file Helper methods for the user model

import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { ClassRole } from '@@/enums'
import type { User, UserCamelCase } from '@@/types'

const ROLE_MAP = {
  owner: __('owner'),
  admin: __('admin'),
  teacher: __('teacher'),
  student: __('student'),
  member: __('member'),
}

enum UserRole {
  OWNER = 'owner',
  TEACHER = 'teacher',
  ADMIN = 'admin',
  STUDENT = 'student',
  MEMBER = 'member',
  DEFAULT = '',
}

const CLASS_ROLE_MAP: Record<ClassRole, string> = {
  [ClassRole.Teacher]: __('Teacher'),
  [ClassRole.Student]: __('Student'),
}

const anonymous = __('Anonymous')
const ANONYMOUS_AVATAR = 'https://padlet.net/avatars/alien1.png' // copied from library/v4/components/OzAvatar.vue
const blankUser = (): User => {
  const dateString = new Date().toISOString()
  return {
    id: -1,
    avatar: ANONYMOUS_AVATAR,
    tenant_id: 1,
    tenant_type: 'native',
    paying: false,
    quota: {
      walls_used: 0,
      walls_limit: 0,
      advertized_walls_limit: 0,
      can_make: false,
    },
    role: UserRole.STUDENT,
    lang: 'en',
    created_at: dateString,
    updated_at: dateString,
    registered_at: dateString,
    name: anonymous,
    email: undefined,
    short_name: undefined,
    registered: false,
    profile_url: undefined,
    days_before_freeze: 0,
    avatar_dominant_rgb: [255, 255, 255],
    is_archive_enabled: !isAppUsing('removeArchive'),
  }
}

function translatedRole(role: UserRole): string {
  return ROLE_MAP[role]
}

function translatedClassRole(role: ClassRole | string | null | undefined): string {
  return CLASS_ROLE_MAP[role as ClassRole] ?? __('Unknown')
}

const displayNameForUser = (user: User): string => {
  return user?.name || user?.username || ''
}

const isAdmin = (user: User): boolean => [UserRole.OWNER, UserRole.ADMIN, UserRole.TEACHER].includes(user.role)

const isOwner = (user: User | UserCamelCase): boolean => user.role === UserRole.OWNER

const isRegistered = (
  user: Partial<Pick<User, 'registered_at' | 'registered'> & Pick<UserCamelCase, 'registeredAt'>>,
): boolean => user.registered_at != null || user.registeredAt != null || Boolean(user.registered)
export {
  ROLE_MAP,
  UserRole,
  anonymous,
  ANONYMOUS_AVATAR,
  blankUser,
  translatedRole,
  translatedClassRole,
  displayNameForUser,
  isAdmin,
  isOwner,
  isRegistered,
}
